import React from "react";
import {autorun} from "mobx";
import {observer} from "mobx-react";

import "./risikoanalyse-popup.scss";

import EsriGraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import {PopupConfiguration} from "../../../core/config/popup-configuration";

import {Button, Dimmer, Loader, Transition} from "semantic-ui-react";
import {Icon} from "../../../core/components/icon/icon";

import {ProjectPerimeterEditor} from "../editor/project-perimeter-editor/project-perimeter-editor";
import {ProjectPerimeterAnalysis} from "../editor/project-perimeter-analysis/project-perimeter-analysis";
import {IAppStore, withStore} from "../../../core/stores/abstract-app-store";
import {RisikoanalyseStore} from "../../../stores/risikoanalyse-store-mobx";

interface RisikoanalysePopupCollapseButtonProps {
    onClick: any;
    visible: boolean;
}

const RisikoanalysePopupCollapseButton = (props: RisikoanalysePopupCollapseButtonProps) => (
    <div className="geo-perimeter-popup-collapse">
        <Button icon size={"mini"} attached="bottom" color={props.visible ? undefined : "orange"}
            className={`geo-perimeter-popup-collapse-button ${props.visible ? "open" : "closed"}`}
            onClick={props.onClick}>
            <Icon name={props.visible ? "navigate_open" : "navigate_close"} />
            {props.visible ? "schliessen" : "Auswertungen"}
        </Button>
    </div>
);

interface RisikoanalysePopupProps {
    config: PopupConfiguration;
    store?: IAppStore; // this is injected by withStore
}

@observer
export class RisikoanalysePopupBase extends React.Component<RisikoanalysePopupProps, any> {
    autorunDisposers: any[] = [];
    pointerRef: any;
    highlightLayer: EsriGraphicsLayer;
    widgetKey = "perimeter-popup";

    constructor(props: RisikoanalysePopupProps) {
        super(props);
        this.state = {
            viewModel: null,
            isLoading: undefined,
            lastItemTitle: "",
            activeIndex: [1],
            elevation: new Map(),
            activePages: [0,1],
        };
        this.pointerRef = React.createRef();
        this.close = this.close.bind(this);
        this.toggleVisibility = this.toggleVisibility.bind(this);
        this.handleToggleEditingPerimeter = this.handleToggleEditingPerimeter.bind(this);

        this.highlightLayer = new EsriGraphicsLayer({listMode: "hide", title: "popup highlight layer"});
    }

    componentDidMount() {
        const {store} = this.props;

        const isDisabled = store?.mapViewerConfig.applicationPopup?.disabled;
        if (isDisabled) {
            return;
        }

        this.autorunDisposers.push(autorun((reaction: any) => {
            if (store?.uiStore.widgets.has(this.widgetKey)) {
                reaction.dispose(); // stop autorun
                return;
            }

            if (!store?.mapStore.view) {
                return;
            }

            store.uiStore.addWidget(this.widgetKey, this);

            this.autorunDisposers.push(autorun((visibilityReaction: any) => {
                if (store.appStore.perimeterPopupVisible) {
                    this.open();
                }
                if (!store.appStore.perimeterPopupVisible) {
                    this.close();
                    visibilityReaction.dispose();
                }}));
            // hide when map popup is opened
            this.autorunDisposers.push(
                autorun(() => {
                    if(store.uiStore.popupVisible) {
                        this.close();
                    }
                })
            );
            reaction.dispose();
        }));
    }

    componentWillUnmount() {
        const {store} = this.props;
        this.autorunDisposers.forEach((disposer: any) => {
            disposer();
        });
        this.autorunDisposers.length = 0;
        store?.uiStore.removeWidget(this.widgetKey);
    }

    // TODO: Funktion und Property auf eigenen ApplicationPopupStore verschieben
    open(): void {
        const {store} = this.props;
        if (!store?.mapStore.view) {
            return;
        }
        if (store.appStore.perimeterPopupDisabled) {
            return;
        }
        this.setState({ isLoading: true});
        if (!this.props.config.dockEnabled) {
            this.close();
        }
    }

    // TODO: Funktion und Property auf eigenen ApplicationPopupStore verschieben
    close(): void {
        const {store} = this.props;
        if (store?.mapStore.view) {
            this.highlightLayer.removeAll();
            // mapStore.selectedFeature(null);
        }

        store?.appStore.setPerimeterPopupVisible(false);
    }

    toggleVisibility(): void {
        const {store} = this.props;
        store?.appStore.setPerimeterPopupVisible(!store?.appStore.perimeterPopupVisible);
        // disable / enable standard popup
        store?.PopupStore.setDisabled(store?.appStore.perimeterPopupVisible);
    }

    render(): JSX.Element | undefined {
        const { isLoading } = this.state;
        const {config, store} = this.props;

        if (!store) {
            return;
        }

        const {mapViewerConfig} = store;
        const risikoanalyse = store as RisikoanalyseStore;

        const editorConfig = mapViewerConfig.applicationPopup?.editor;
        const apiLayersConfig = mapViewerConfig.api.layers;

        if (mapViewerConfig.applicationPopup?.disabled) {
            return;
        }

        const popupData = (
            <div className="geo-perimeter-popup-data">
                {risikoanalyse.loading &&
                    <Dimmer active inverted>
                        <Loader inverted>Loading</Loader>
                    </Dimmer>
                }
                {store.appStore.perimeterPopupVisible &&
                <div className="geo-perimeter-popup-data-content">
                    <ProjectPerimeterEditor
                        appStore={store.appStore}
                        risikoanalyse={risikoanalyse}
                        config={editorConfig}
                        mapViewerConfig={mapViewerConfig}
                        layers={apiLayersConfig}
                        mapStore={store.mapStore}
                        uiStore={store.uiStore} />
                    <ProjectPerimeterAnalysis
                        appStore={store.appStore}
                        uiStore={store.uiStore}
                        mapStore={store.mapStore}
                        risikoanalyse={risikoanalyse} />
                </div>
                }
            </div>
        );

        let cssClassPopup: string = "geo-perimeter-popup-wrapper geo-component geo-widget";
        if (store.appStore.perimeterPopupVisible) {
            cssClassPopup += " open";
        } else {
            cssClassPopup += " closed";
        }
        const cssClassContent: string = "geo-perimeter-popup-content";

        return (
            <div className={cssClassPopup}>
                <div className={"geo-perimeter-popup-spinner"
                    + (isLoading!==undefined?(isLoading? " geo-spinner-visible" : " geo-spinner-hidden"):"")}
                >
                    <Icon name={"recycle"} className={"geo-rotating"} selected={true}/>
                </div>
                <Transition visible={store.appStore.perimeterPopupVisible} duration={0}
                    mountOnShow={false} animation="fly down">
                    <div className={"geo-perimeter-popup docked docked-" + config.dockOptions.position}>
                        <div className={cssClassContent}>
                            <div onClick={this.toggleVisibility} className={"geo-close"}><Icon name="navigate_cross" />
                            </div>
                            <div className="geo-perimeter-popup-title">
                                {"Bereich analysieren"}
                            </div>
                            {popupData}
                        </div>
                    </div>
                </Transition>
                <RisikoanalysePopupCollapseButton
                    onClick={this.toggleVisibility}
                    visible={(store.appStore.perimeterPopupVisible)}
                />
            </div>
        );
    }

    private handleToggleEditingPerimeter(): void {
        const {store} = this.props;
        const risikoanalyse = store as RisikoanalyseStore;
        const {isEditing} = risikoanalyse;
        if (isEditing) {
            risikoanalyse.setEditing(false);
        } else {
            risikoanalyse.setEditing(true, "perimeter");
        }
    }
}

export const RisikoanalysePopup = withStore(RisikoanalysePopupBase);
